import React from 'react';
import {fetchFileHighlights, deleteFileHighlight, createFileHighlight} from './sourcesApi';

/**
 * Fetches and manages highlights for a My Data file.
 *
 * @param {string|number|null} fileId             - The data item id (numeric, as string) to load highlights for.
 * @param {string|null}        reportId           - Optional report context to filter highlights.
 * @param {number|null}        numericDataItemId  - Explicit numeric DB id for creation (falls back to fileId).
 * @param {string|null}        userId             - Viewer id; stamped as the highlight owner on create so it is scoped to this user for search.
 * @returns {{ highlights, isLoading, error, createHighlight, deleteHighlight, refetch }}
 */
export default function useFileHighlights(fileId, reportId = null, numericDataItemId = null, userId = null) {
    const [highlights, setHighlights] = React.useState([]);
    const [isLoading, setIsLoading] = React.useState(false);
    const [error, setError] = React.useState(null);
    const fetchCountRef = React.useRef(0);

    const load = React.useCallback((id, rid, signal) => {
        if (!id) {
            setHighlights([]);
            setIsLoading(false);
            setError(null);
            return;
        }
        setIsLoading(true);
        setError(null);
        const token = ++fetchCountRef.current;
        fetchFileHighlights({dataItemId: id, reportId: rid || null, signal})
            .then((items) => {
                if (token !== fetchCountRef.current) return;
                setHighlights(Array.isArray(items) ? items : []);
            })
            .catch((err) => {
                if (err?.name === 'AbortError' || token !== fetchCountRef.current) return;
                console.error('[useFileHighlights] Failed to load highlights:', err);
                setError(err?.message || 'Failed to load highlights.');
                setHighlights([]);
            })
            .finally(() => {
                if (token === fetchCountRef.current) setIsLoading(false);
            });
    }, []);

    React.useEffect(() => {
        const controller = new AbortController();
        load(fileId, reportId, controller.signal);
        return () => controller.abort();
    }, [fileId, reportId, load]);

    const deleteHighlight = React.useCallback(async (highlightId) => {
        try {
            await deleteFileHighlight({highlightId});
            setHighlights((prev) => prev.filter((h) => h.id !== highlightId));
        } catch (err) {
            console.error('[useFileHighlights] Failed to delete highlight:', err);
        }
    }, []);

    const createHighlight = React.useCallback(async ({segmentId, regionType = 'FULL_SEGMENT', regionData = null, comment = null} = {}) => {
        const dataItemId = numericDataItemId || fileId;
        if (!dataItemId || !segmentId) return null;
        try {
            const created = await createFileHighlight({dataItemId, segmentId, regionType, regionData, comment, userId});
            setHighlights((prev) => [...prev, created]);
            return created;
        } catch (err) {
            console.error('[useFileHighlights] Failed to create highlight:', err);
            return null;
        }
    }, [numericDataItemId, fileId, userId]);

    const refetch = React.useCallback(() => {
        const controller = new AbortController();
        load(fileId, reportId, controller.signal);
    }, [fileId, reportId, load]);

    return {fileId: fileId || null, highlights, isLoading, error, createHighlight, deleteHighlight, refetch};
}
